import { ImageOff } from "lucide-react";
import { Link } from "@/lib/nav";
import { useCallback, useState } from "react";

import { cn } from "@/lib/utils";
import { photoFailureOf, type PhotoAudience, type PhotoFailure } from "@buildpy/core/modules/project/photo-view";
import { LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";

// img onError 는 응답 코드를 모른다 — 실패하면 같은 주소에 HEAD 로 한 번 더 물어 코드를 얻는다.
const ask = async (src: string, audience: PhotoAudience): Promise<PhotoFailure> => {
  try {
    const res = await fetch(src, { method: "HEAD", cache: "no-store", credentials: "same-origin" });
    return photoFailureOf(res.status, audience, res.headers.get("x-buildpy-missing") === "1");
  } catch {
    return photoFailureOf(null, audience);
  }
};

const PROBE_TTL_MS = 10_000;
const probes = new Map<string, { at: number; result: Promise<PhotoFailure> }>();

// 같은 사진을 격자·크게 보기가 함께 물어도 10초 동안은 첫 답을 다시 쓴다 — 그 사이 다시 로그인해도 10초가 지나야 새로 묻는다.
const probe = (src: string, audience: PhotoAudience): Promise<PhotoFailure> => {
  const key = `${audience} ${src}`;
  const now = Date.now();
  const hit = probes.get(key);
  if (hit && now - hit.at < PROBE_TTL_MS) return hit.result;
  for (const [k, v] of probes) if (now - v.at >= PROBE_TTL_MS) probes.delete(k);
  const result = ask(src, audience);
  probes.set(key, { at: now, result });
  return result;
};

// 상태: null = 정상 · "checking" = 실패 후 코드 확인 중 · PhotoFailure = 확인 끝.
export const usePhotoFailure = (src: string, audience: PhotoAudience) => {
  const [failure, setFailure] = useState<PhotoFailure | "checking" | null>(null);
  const onError = useCallback(() => {
    setFailure("checking");
    void probe(src, audience).then(setFailure);
  }, [src, audience]);
  return { failure, onError };
};

interface Props {
  failure: PhotoFailure | "checking";
  className?: string;
  // 버튼·링크 안에 놓일 때는 false — 대화형 요소 안에 링크를 넣지 않는다.
  withLink?: boolean;
}

// 깨진 그림 대신 자리표시 — 세션 만료면 로그인 링크까지.
const PhotoFallback = ({ failure, className, withLink = true }: Props) => (
  <span
    className={cn("flex flex-col items-center justify-center gap-y-1 bg-muted p-1 text-center text-xs text-muted-foreground", className)}
    data-kind={failure === "checking" ? "checking" : failure.kind}
    data-testid="photo-fallback">
    <ImageOff aria-hidden className="size-5 shrink-0" />
    <span aria-live="polite">{failure === "checking" ? "확인 중…" : failure.text}</span>
    {failure !== "checking" && withLink && failure.kind === "login" && (
      <Link className="font-medium text-primary underline-offset-2 hover:underline" href={LOGIN_PATH}>
        다시 로그인
      </Link>
    )}
  </span>
);

export default PhotoFallback;
